/**
 * VersionInfo — renders the running build's version, build number,
 * commit SHA, deploy timestamp, and auto-generated change list for both
 * the frontend bundle and the backend image.
 *
 * Frontend version/build/commit/changes are baked into the bundle via
 * Vite's `define` block (see vite.config.ts) and read here as
 * compile-time constants — no network call needed for those.
 * `deployedAt` is the exception: it lives on the environment (written
 * to .env.deploy at SSM rollout time), so both the Frontend and Server
 * lines pick it up from the server's /api/build-info response — the
 * server is the source of truth for "when did this environment last
 * receive a deploy."
 *
 * Two lines are intentional: when a deploy is half-applied (new
 * server image, stale frontend dist) the divergence is self-
 * documenting. ottotester#187 locked this layout; #188 added
 * deployedAt as the per-environment signal; #677 added the build
 * number and dropped the redundant "Built" timestamp row — `builtAt`
 * and `deployedAt` are always seconds apart (the pipeline rebuilds on
 * every deploy), so `Deployed` carries the per-environment "since
 * when" and the SHA + build number already pin the artifact. The
 * `builtAt` field still rides along in /api/build-info; only the panel
 * row is gone. #683 added the expandable change-list section below the
 * version lines — the server's change list is the one shown (the
 * server is the source of truth for what shipped to this environment).
 *
 * Under the version lines sits the release line: whether a newer
 * OttoTester release is out, when the install last checked, a Release
 * notes link and a Check for updates button. The server decides which
 * state it is in (`release` on the admin settings read), so this panel
 * never compares versions. The notes themselves live only on the docs
 * site; the panel links there and never shows their text.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CopyIconButton } from '@/components/CopyIconButton'
import { DOCS_BASE_URL } from '@/help/HelpPanel'
import {
  adminAppSettingsAPI,
  adminScheduledJobsAPI,
  type ReleaseStatus,
} from '@/lib/api'

/** The auto-generated change list (ottotester#683 slice 2). */
type ChangeList = {
  /** Last release tag the list is diffed against — empty when no tag
   * is reachable (e.g. a `.git`-less source tree). */
  since: string
  groups: { new: string[]; fixed: string[]; other: string[] }
  /** Count of entries beyond the display cap — rendered as "and N more". */
  overflow: number
}

type BuildInfo = {
  version: string
  /** Total commit count of the built ref — monotonic and comparable
   * across localhost / dev / prod (ottotester#677). */
  build: number
  commit: string
  builtAt: string
  deployedAt: string
  /** Auto-generated change list since the last release (#683). */
  changes: ChangeList
}

const EMPTY_CHANGES: ChangeList = {
  since: '',
  groups: { new: [], fixed: [], other: [] },
  overflow: 0,
}

/** Parse the bundled __BUILD_CHANGES__ constant — a JSON string Vite
 * bakes in (see vite.config.ts). Guarded: a malformed value yields an
 * empty change list rather than crashing the panel. */
function parseBundledChanges(): ChangeList {
  try {
    const parsed = JSON.parse(__BUILD_CHANGES__) as unknown
    return normalizeChanges(parsed)
  } catch {
    return EMPTY_CHANGES
  }
}

/** Narrow an unknown value to a ChangeList, filling defaults for any
 * missing / malformed field. Used for both the bundled constant and the
 * server fetch response. */
function normalizeChanges(value: unknown): ChangeList {
  if (!value || typeof value !== 'object') return EMPTY_CHANGES
  const v = value as Record<string, unknown>
  const g = (v.groups && typeof v.groups === 'object' ? v.groups : {}) as Record<
    string,
    unknown
  >
  const arr = (x: unknown): string[] =>
    Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : []
  return {
    since: typeof v.since === 'string' ? v.since : '',
    groups: { new: arr(g.new), fixed: arr(g.fixed), other: arr(g.other) },
    overflow:
      typeof v.overflow === 'number' && Number.isInteger(v.overflow) && v.overflow >= 0
        ? v.overflow
        : 0,
  }
}

const FRONTEND_BUILD: Omit<BuildInfo, 'deployedAt'> = {
  version: __BUILD_VERSION__,
  // Non-negative integer only — the value is a commit count. The
  // digits-only test rejects trailing garbage and negatives that a
  // bare `parseInt` would silently coerce. (vite.config already
  // normalizes it; this is defense-in-depth at the consuming end.)
  build: /^\d+$/.test(__BUILD_NUMBER__) ? Number(__BUILD_NUMBER__) : 0,
  commit: __BUILD_COMMIT__,
  builtAt: __BUILD_TIMESTAMP__,
  changes: parseBundledChanges(),
}

const FALLBACK_SERVER_BUILD: BuildInfo = {
  version: 'dev',
  build: 0,
  commit: 'local',
  builtAt: '',
  deployedAt: '',
  changes: EMPTY_CHANGES,
}

/** Total entry count across all three groups — drives the collapsed
 * summary line ("7 changes since v0.4.0"). */
function changeCount(c: ChangeList): number {
  return c.groups.new.length + c.groups.fixed.length + c.groups.other.length
}

function formatTimestamp(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  // YYYY-MM-DD HH:MM UTC — short, sortable, deterministic. Matches the
  // example shown in ottotester#187.
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  )
}

const RELEASE_NOTES_URL = `${DOCS_BASE_URL}/release-notes/`
const UPDATE_COMMAND = './ottotester update'
/** How many times to re-read the release state after Check for updates
 * starts the check. The check gives GitHub 10 seconds, so this waits a
 * little longer than that before giving up. */
const CHECK_POLL_TRIES = 15

/** A calendar day in UTC, e.g. "November 2, 2026". */
function formatDay(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** When something happened, in UTC like the version lines: "just now",
 * "today at 14:05 UTC", "yesterday at 14:05 UTC" or "on October 26, 2026
 * at 14:05 UTC". */
export function describeWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  if (Math.abs(now.getTime() - d.getTime()) < 60_000) return 'just now'
  const pad = (n: number) => String(n).padStart(2, '0')
  const time = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  const dayStart = (x: Date) => Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate())
  const days = Math.round((dayStart(now) - dayStart(d)) / 86_400_000)
  if (days === 0) return `today at ${time}`
  if (days === 1) return `yesterday at ${time}`
  return `on ${formatDay(d)} at ${time}`
}

/** The release line under the version numbers. One row in every state,
 * each with the Release notes link. The button shows only where pressing
 * it can make a request. */
function ReleaseLine({
  status,
  checking,
  onCheck,
}: {
  status: ReleaseStatus
  checking: boolean
  onCheck: () => void
}) {
  const canCheck =
    status.state === 'newer' ||
    status.state === 'current' ||
    status.state === 'never-checked' ||
    status.state === 'failed'
  const running = status.running ?? ''

  let dot = 'bg-muted-foreground'
  let main: ReactNode
  let when: string | null = null
  if (checking) {
    main = 'Checking GitHub for a newer release…'
    when = status.checkedAt ? `Last checked ${describeWhen(status.checkedAt)}` : null
  } else {
    switch (status.state) {
      case 'newer':
        dot = 'bg-primary'
        main = (
          <>
            <b>{status.latest?.version} is out.</b> You're on {running}.
          </>
        )
        // A later check that failed does not hide the release, but it is
        // said: when the last good check ran, and why the latest one didn't.
        if (status.error && status.checkedAt) {
          when = [
            status.succeededAt ? `Last successful check: ${describeWhen(status.succeededAt)}.` : null,
            `Couldn't check again ${describeWhen(status.checkedAt)}: ${status.error}`,
          ]
            .filter(Boolean)
            .join(' ')
        } else {
          when = status.succeededAt ? `Last checked ${describeWhen(status.succeededAt)}` : null
        }
        break
      case 'current':
        dot = 'bg-success'
        main = `You're on the newest release, ${running}.`
        when = status.checkedAt ? `Last checked ${describeWhen(status.checkedAt)}` : null
        break
      case 'never-checked':
        main = 'Not checked for a newer release yet.'
        when = 'This install checks once a day.'
        break
      case 'failed':
        dot = 'bg-amber-500'
        main = ["Couldn't check for a newer release.", status.error, 'Try again later.']
          .filter(Boolean)
          .join(' ')
        when = status.succeededAt
          ? `Last successful check: ${describeWhen(status.succeededAt)}`
          : 'No check has reached GitHub yet.'
        break
      case 'off':
        main = 'Checking for new releases is turned off on this install.'
        when =
          "To turn it on, set RELEASE_CHECK_ENABLED=true in the install's settings file and restart OttoTester."
        break
      case 'not-set-up':
        main = "This copy of OttoTester doesn't check for new releases."
        when = 'It has no public release list to check yet.'
        break
      case 'dev':
        main = "This is a development build, not a release, so it doesn't check for newer releases."
        when = 'Only installs running an exact release, like v1.2.3, check.'
        break
    }
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border bg-card px-3 py-2.5 text-[13px]"
      data-state={checking ? 'checking' : status.state}
    >
      {checking ? (
        <Loader2
          className="h-3.5 w-3.5 shrink-0 animate-spin text-primary motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : (
        <span className={`mt-1.5 h-2 w-2 shrink-0 self-start rounded-full ${dot}`} aria-hidden="true" />
      )}
      <div className="grid min-w-0 flex-[1_1_280px] gap-0.5" role="status">
        <span>{main}</span>
        {when && <span className="text-xs text-muted-foreground">{when}</span>}
      </div>
      <a
        href={RELEASE_NOTES_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="whitespace-nowrap text-[12.5px] text-primary hover:underline"
      >
        Release notes
      </a>
      {canCheck && (
        <Button type="button" variant="outline" size="sm" onClick={onCheck} disabled={checking}>
          {checking ? (
            'Checking…'
          ) : (
            <>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              Check for updates
            </>
          )}
        </Button>
      )}
    </div>
  )
}

/** Shown only when a newer release is out: its version and date, a link
 * to its section of the Release notes page, and the command that
 * installs it. None of the notes' own text. */
function NewerRelease({ latest }: { latest: { version: string; publishedAt: string | null } }) {
  const published = latest.publishedAt ? new Date(latest.publishedAt) : null
  return (
    <section
      aria-label="Newer release"
      className="grid gap-2.5 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3.5"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="text-[15px]">OttoTester {latest.version} is available</strong>
        {published && !Number.isNaN(published.getTime()) && (
          <span className="text-xs text-muted-foreground">Released {formatDay(published)}</span>
        )}
      </div>
      <a
        href={`${RELEASE_NOTES_URL}#${latest.version}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-fit text-sm text-primary hover:underline"
      >
        See what's new in {latest.version}
      </a>
      <div className="text-xs">To update, run this in your install folder:</div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded-md border bg-card px-2.5 py-1 font-mono text-[12.5px]">
          {UPDATE_COMMAND}
        </code>
        <CopyIconButton value={UPDATE_COMMAND} label="Copy" title="Copy the update command" />
      </div>
      <div className="text-xs text-muted-foreground">
        If the new version doesn't start, the update puts this one back.
      </div>
    </section>
  )
}

function Line({ label, info }: { label: string; info: BuildInfo }) {
  return (
    <div className="font-mono text-xs text-muted-foreground">
      <span className="font-sans text-foreground">{label}</span>{' '}
      {info.version} <span aria-hidden="true">·</span> build {info.build}{' '}
      <span aria-hidden="true">·</span> {info.commit}{' '}
      <span aria-hidden="true">·</span> deployed{' '}
      {formatTimestamp(info.deployedAt)}
    </div>
  )
}

/** One labelled bucket of change entries (New / Fixed / Other). Renders
 * nothing when the bucket is empty. */
function ChangeGroup({ label, entries }: { label: string; entries: string[] }) {
  if (entries.length === 0) return null
  return (
    <div className="space-y-0.5">
      <div className="text-xs font-medium text-foreground">{label}</div>
      <ul className="space-y-0.5">
        {entries.map((entry, i) => (
          <li
            key={`${label}-${i}`}
            className="font-mono text-xs text-muted-foreground"
          >
            {entry}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Expandable change-list section — the auto-generated release notes
 * (ottotester#683 slice 2). Collapsed: a one-line summary ("7 changes
 * since v0.4.0"). Expanded: the New / Fixed / Other buckets, plus an
 * "and N more" line when entries were capped. When there are no
 * changes (HEAD on the release tag, or a `.git`-less build) it renders
 * a quiet "No changes since vX.Y.Z" line instead, with no toggle. It
 * says "No changes", not "Up to date": it only means nothing changed
 * since that tag, and beside "v1.1.11 is out" an "Up to date" would
 * read as a contradiction.
 */
function Changes({ changes }: { changes: ChangeList }) {
  const [expanded, setExpanded] = useState(false)
  const total = changeCount(changes)

  if (total === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        No changes{changes.since ? ` since ${changes.since}` : ''}
      </div>
    )
  }

  const sinceLabel = changes.since ? ` since ${changes.since}` : ''
  const summary = `${total} change${total === 1 ? '' : 's'}${sinceLabel}`

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex items-center gap-1 text-xs text-foreground hover:underline"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        )}
        {summary}
      </button>
      {expanded && (
        <div className="space-y-2 pl-4">
          <ChangeGroup label="New" entries={changes.groups.new} />
          <ChangeGroup label="Fixed" entries={changes.groups.fixed} />
          <ChangeGroup label="Other" entries={changes.groups.other} />
          {changes.overflow > 0 && (
            <div className="text-xs text-muted-foreground">
              and {changes.overflow} more
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export type VersionInfoProps = {
  /** Pre-resolved frontend metadata. Tests pass this in directly to
   * exercise the rendering without depending on Vite's define. In
   * production it defaults to the bundled constants for
   * version/build/commit/builtAt; deployedAt comes from the server
   * fetch (or the prop, if supplied). */
  frontend?: Partial<BuildInfo>
  /** Pre-resolved server metadata. When omitted, the component
   * fetches /api/build-info and renders fallback defaults until the
   * response lands (or on fetch failure). */
  server?: BuildInfo
  /** Override the server fetch URL. Defaults to /api/build-info. Used
   * by tests to point at a mock. */
  serverEndpoint?: string
  /** The release line's state. When omitted the panel reads it from the
   * admin settings route; tests pass it in. `null` shows no release line,
   * as with a server too old to send one. */
  release?: ReleaseStatus | null
  /** Wait between re-reads while Check for updates runs, in ms. Tests
   * pass 0. */
  checkPollMs?: number
}

export function VersionInfo({
  frontend,
  server,
  serverEndpoint = '/api/build-info',
  release: releaseProp,
  checkPollMs = 1000,
}: VersionInfoProps = {}) {
  const [serverInfo, setServerInfo] = useState<BuildInfo>(
    server ?? FALLBACK_SERVER_BUILD,
  )
  const [release, setRelease] = useState<ReleaseStatus | null>(releaseProp ?? null)
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState<string | null>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (releaseProp !== undefined) return
    let cancelled = false
    adminAppSettingsAPI
      .get()
      .then((res) => {
        if (!cancelled) setRelease(res.release ?? null)
      })
      .catch(() => {
        // No release line rather than a broken panel.
      })
    return () => {
      cancelled = true
    }
  }, [releaseProp])

  /** Runs the scheduled release check now, then re-reads until it has
   * recorded a new check or the tries run out. */
  async function checkNow(): Promise<void> {
    if (!release || checking) return
    const before = release.checkedAt
    setChecking(true)
    setCheckError(null)
    try {
      await adminScheduledJobsAPI.runNow(release.jobId)
    } catch (e) {
      if (mounted.current) {
        setCheckError(`Couldn't start the check: ${e instanceof Error ? e.message : String(e)}`)
        setChecking(false)
      }
      return
    }
    try {
      for (let i = 0; i < CHECK_POLL_TRIES && mounted.current; i++) {
        await new Promise((resolve) => setTimeout(resolve, checkPollMs))
        const next = (await adminAppSettingsAPI.get()).release
        if (next && next.checkedAt !== before) {
          if (mounted.current) setRelease(next)
          break
        }
      }
    } catch {
      // Keep the line as it was; the daily check records the next result.
    } finally {
      if (mounted.current) setChecking(false)
    }
  }

  useEffect(() => {
    if (server) return
    let cancelled = false
    fetch(serverEndpoint, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return
        const data = (await res.json()) as Partial<BuildInfo>
        if (cancelled) return
        setServerInfo({
          version: data.version || FALLBACK_SERVER_BUILD.version,
          build:
            typeof data.build === 'number'
              ? data.build
              : FALLBACK_SERVER_BUILD.build,
          commit: data.commit || FALLBACK_SERVER_BUILD.commit,
          builtAt: data.builtAt || FALLBACK_SERVER_BUILD.builtAt,
          deployedAt: data.deployedAt || FALLBACK_SERVER_BUILD.deployedAt,
          // Guarded — a missing / malformed `changes` field yields an
          // empty change list rather than crashing the panel.
          changes: normalizeChanges(data.changes),
        })
      })
      .catch(() => {
        // Leave fallback in place. Component never blanks.
      })
    return () => {
      cancelled = true
    }
  }, [server, serverEndpoint])

  // Frontend's deployedAt mirrors the server's — they deployed
  // together, the server is the source of truth. If the caller
  // passed an explicit `frontend.deployedAt`, that wins (tests).
  const frontendInfo: BuildInfo = {
    version: frontend?.version ?? FRONTEND_BUILD.version,
    build: frontend?.build ?? FRONTEND_BUILD.build,
    commit: frontend?.commit ?? FRONTEND_BUILD.commit,
    builtAt: frontend?.builtAt ?? FRONTEND_BUILD.builtAt,
    deployedAt: frontend?.deployedAt ?? serverInfo.deployedAt,
    changes: frontend?.changes ?? FRONTEND_BUILD.changes,
  }

  // The change list shown is the server's — the server is the source
  // of truth for what shipped to this environment. (Frontend and
  // server change lists agree in a clean deploy; on a half-applied
  // deploy the version lines above already surface the divergence.)
  return (
    <section aria-label="Build version" className="space-y-3">
      <div className="space-y-1">
        <Line label="Frontend" info={frontendInfo} />
        <Line label="Server" info={serverInfo} />
      </div>
      {release && (
        <ReleaseLine status={release} checking={checking} onCheck={() => void checkNow()} />
      )}
      {checkError && (
        <p className="text-xs text-destructive" role="alert">
          {checkError}
        </p>
      )}
      {release?.state === 'newer' && release.latest && !checking && (
        <NewerRelease latest={release.latest} />
      )}
      <Changes changes={serverInfo.changes} />
    </section>
  )
}
